import React, { useContext, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';
import AuthContext from '../../contexts/AuthContext';

export default function EditUserRoleDialog({ open, onClose, organizationId, organization, user, onUpdated }) {
  const { tokens, authData } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const [job, setJob] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isManager = authData?.user?.role === 'Manager';
  const isLabDirectorAtOrg = Boolean(
    authData?.jobs_at_organizations?.some(
      (j) => j.organization_id === organizationId && j.job === 'LabDirector'
    )
  );
  const jobOptions = React.useMemo(() => {
    if (organization?.is_lab) {
      const opts = ['LabStaff', 'Accessioner'];
      if (isManager || isLabDirectorAtOrg) opts.unshift('LabDirector');
      return opts;
    }
    const opts = [];
    if (organization?.is_practice) opts.push('Provider', 'OfficeStaff');
    if (organization?.is_collection_site) opts.push('Phlebotomist');
    return opts.length ? opts : ['Provider', 'OfficeStaff'];
  }, [organization, isManager, isLabDirectorAtOrg]);

  useEffect(() => {
    if (open && user) {
      const initialJob = user.job || '';
      setJob(jobOptions.includes(initialJob) ? initialJob : (jobOptions[0] || ''));
      setError('');
      setSaving(false);
    }
  }, [open, user, jobOptions]);

  const handleSave = async () => {
    if (!job) {
      setError('Job is required');
      return;
    }
    if (!jobOptions.includes(job)) {
      setError('Invalid job for this organization');
      return;
    }
    setError('');
    try {
      setSaving(true);
      const res = await fetch(
        `${apiBase}/organizations/${organizationId}/users/${user.job_at_org_id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens?.id_token}`,
          },
          body: JSON.stringify({ job }),
        }
      );
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'Failed to update job');
      }
      if (onUpdated) onUpdated({ ...user, job });
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to update job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit User Job</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Change the job for {user?.name || user?.email} at this organization.
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="edit-job-label">Job</InputLabel>
          <Select
            labelId="edit-job-label"
            value={job}
            label="Job"
            onChange={(e) => setJob(e.target.value)}
          >
            {jobOptions.map((j) => (
              <MenuItem key={j} value={j}>{j}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
